function cn(...cs) { return cs.filter(Boolean).join(" "); }

export default function EmptyState({ clientData }) {
    const firstName = clientData?.firstName;
    const domains = clientData?.domains || [];

    const checklist = [
        { label: "Account created", done: true },
        {
            label: domains.length > 0
                ? `${domains.length} website${domains.length !== 1 ? "s" : ""} connected`
                : "Website connected",
            done: domains.length > 0,
        },
        { label: "SEO audit queued — results in ~5 min", done: false, active: true },
        { label: "Strategist will reach out within 24 hrs", done: false },
        { label: "First keyword campaign setup", done: false },
    ];

    return (
        <main className="flex-1 flex flex-col items-center justify-center bg-gray-50 px-6">
            <div className="w-full max-w-sm">
                {/* Logo mark */}
                <div className="flex justify-center mb-6">
                    <div className="relative">
                        <div className="w-16 h-16 rounded-2xl bg-orange-500 flex items-center justify-center shadow-lg shadow-orange-200">
                            <svg width="30" height="30" viewBox="0 0 100 100" fill="white">
                                <path d="M78 67c0 0-1-5-9-7l-37-7c-1 0-1-1 0-1l37-7c8-2 9-7 9-7s1-7-7-10L29 12c-6-2-13 0-16 6L5 45c-3 6-1 13 5 16l18 8v5c0 7 5 12 12 12h38c8 0 12-7 9-13z"/>
                            </svg>
                        </div>
                        {/* Pulse ring */}
                        <div className="absolute inset-0 rounded-2xl bg-orange-400 opacity-30 animate-ping"/>
                    </div>
                </div>

                {/* Heading */}
                <h1 className="text-2xl font-bold text-gray-900 text-center mb-2" style={{ fontFamily: "'Georgia', serif" }}>
                    {firstName ? `Welcome, ${firstName}!` : "Welcome aboard!"}
                </h1>
                <p className="text-sm text-gray-500 text-center mb-8 leading-relaxed">
                    Your dashboard is being set up. We're running your first SEO audit
                    {domains.length > 0 && ` for ${domains[0]}`} now — this usually takes a few minutes.
                </p>

                {/* Checklist card */}
                <div className="bg-white border border-gray-200 rounded-2xl p-5 mb-5 shadow-sm">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">Getting started</p>
                    <div className="space-y-3">
                        {checklist.map((item, i) => (
                            <div key={i} className="flex items-center gap-3">
                                <div className={cn(
                                    "w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 border-2",
                                    item.done
                                        ? "bg-green-500 border-green-500"
                                        : item.active
                                            ? "border-orange-400 bg-orange-50"
                                            : "border-gray-200 bg-white"
                                )}>
                                    {item.done && (
                                        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round">
                                            <polyline points="20 6 9 17 4 12"/>
                                        </svg>
                                    )}
                                    {item.active && (
                                        <div className="w-2 h-2 rounded-full bg-orange-400 animate-pulse"/>
                                    )}
                                </div>
                                <span className={cn(
                                    "text-sm",
                                    item.done ? "text-gray-400 line-through" : item.active ? "text-orange-600 font-medium" : "text-gray-600"
                                )}>
                                    {item.label}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Domains preview */}
                {domains.length > 0 && (
                    <div className="bg-orange-50 border border-orange-100 rounded-2xl p-4 mb-5">
                        <p className="text-xs font-semibold text-orange-600 uppercase tracking-wider mb-3">Sites being audited</p>
                        <div className="space-y-2">
                            {domains.map(d => (
                                <div key={d} className="flex items-center gap-2.5">
                                    <div className="w-2 h-2 rounded-full bg-orange-400 animate-pulse flex-shrink-0"/>
                                    <span className="text-sm text-gray-700 font-medium">{d}</span>
                                    <span className="ml-auto text-xs text-orange-500 font-medium">Auditing…</span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Support link */}
                <p className="text-xs text-gray-400 text-center">
                    Questions?{" "}
                    <button className="text-orange-500 hover:underline font-medium">Contact your strategist</button>
                </p>
            </div>
        </main>
    );
}
